import React, { useCallback, useMemo } from 'react';
import { Button, Col, Form, Spinner } from 'react-bootstrap';
import { useFormik } from 'formik';
import { FormField } from './FormField';
import {
    formatReqUser,
    getValidationSchemaForFields,
    isNominationWithTranscriptionNeeded,
    isNominationWithTranscriptionRequired,
} from '../../../util';
import { useEscCallback } from './utils';

import './form-field.css';

const REQ_USER_FIELDS = [
    {
        id: 'userId',
        name: 'ID',
        description: 'Укажите ID или email пользователя, которого хотите добавить в заявку',
        inputType: 'text',
        required: true,
    },
    {
        id: 'surname',
        name: 'Фамилия',
        description: 'Должна полностью совпадать с фамилией, указанной участником в своем профиле',
        inputType: 'rustext',
        required: true,
    },
    {
        id: 'name',
        name: 'Имя',
        description: 'Должно полностью совпадать с именем, указанным участником в своем профиле',
        inputType: 'rustext',
        required: true,
    },
    {
        id: 'role',
        name: 'Роль',
        description: 'Участником является выступающий на сцене. Остальные - помощники',
        inputType: 'role',
        required: true,
    },
    {
        id: 'desc',
        name: 'Пояснение',
        description: 'Для помощника - для чего он нужен. Пример: "помощник нужен для нанесения сложного грима"',
        inputType: 'text',
        required: true,
    },
    {
        id: 'descTrans',
        name: 'Транскрипция',
        description: '',
        inputType: 'text',
        required: false,
    },
];
const descDescriptions = {
    DEFILE:
        'Для косплеера (участника) указать имя персонажа. Для помощника - для чего он нужен. ' +
        'Пример: "Naruto" или "помощник нужен для нанесения сложного грима"',
    DANCE:
        'Для танцора (участника) так и написать - "Танцор". Для помощника указать для чего он нужен. ' +
        'Например: "помощник нужен для нанесения сложного грима"',
    KARAOKE:
        'Для выступающего (участника) указать его роль: вокалист или подтанцовка. ' +
        'Для помощника - для чего он нужен. Например: "помощник нужен для нанесения сложного грима"',
    SCENKA:
        'Для выступающего (участника) указать имя персонажа и исходник (если их несколько). ' +
        'Для помощника - для чего он нужен. Пример: "Наруто из аниме Naruto" ' +
        'или "помощник нужен для помощи со сложными декорациями"',
};
const descUchDescriptions = {
    DEFILE: 'Указать имя персонажа. Пример: "Naruto"',
    DANCE: 'Для танцора (участника) так и написать - "Танцор".',
    KARAOKE: 'Для выступающего (участника) указать его роль: вокалист или подтанцовка.',
    SCENKA:
        'Для выступающего (участника) указать имя персонажа и исходник (если их несколько). ' +
        'Пример: "Наруто из аниме Naruto"',
    MASTER: 'Укажите роль участника: ведущий, лектор, помогает с чем-то и т.д.',
    ACCREDITATION: 'Можно указать просто - фотограф или оператор. Либо поясните роль участника.',
    SPECIAL_PRIZE: 'Можно указать просто - представитель. Либо поясните роль участника.',
};
const transDescriptions = {
    few:
        'Укажите транскрипцию имени персонажа или русский вариант имени. ' +
        'Используется в программе ведущего для объявления выступления. Указывайте также ударение. ' +
        'Например: [усАги цукИно]',
    many:
        'Если в заявке 3 или менее участников, необходимо указать транскрипцию имени персонажа ' +
        'или русский вариант имени. Используется в программе ведущего для объявления выступления. ' +
        'Если участников более 3х, транскрипцию можно не заполнять (для больших групп персонажей ' +
        'не объявляют). Указывайте также ударение. Например: [усАги цукИно]',
};

const reqUserField = {
    id: 'reqUser',
    name: 'Участник',
    inputType: 'text',
};

function useInitData(reqUser, nomination) {
    return useMemo(() => {
        const reqUserFields = [];
        if (!reqUser) {
            reqUserFields.push(REQ_USER_FIELDS[0], REQ_USER_FIELDS[1], REQ_USER_FIELDS[2]);
        }
        reqUserFields.push(REQ_USER_FIELDS[3]);
        reqUserFields.push({ ...REQ_USER_FIELDS[4], description: descDescriptions[nomination.type] });
        reqUserFields.push({ ...REQ_USER_FIELDS[5] });
        reqUserFields[reqUserFields.length - 1].required = isNominationWithTranscriptionRequired(nomination);
        const schema = getValidationSchemaForFields(reqUserFields);
        const initialData = reqUser
            ? {
                role: reqUser.role,
                desc: reqUser.description,
                descTrans: reqUser.transcription,
            }
            : {};
        return [reqUserFields, schema, initialData];
    }, [reqUser, nomination]);
}

function useFormFields(fields, values, request, reqUser) {
    const isNewReqUser = !reqUser;
    return useMemo(() => {
        const nomination = request.nomination;
        const needTranscription = isNominationWithTranscriptionNeeded(nomination);
        const participantsCount = request.users.filter(data => data.role === 'participant').length;
        const roleField = fields.find((field) => field.id === 'role');
        const role = values && values[roleField.id];
        const descField = fields.find((field) => field.id === 'desc');
        if (['DEFILE', 'SCENKA', 'KARAOKE', 'DANCE'].includes(nomination.type)) {
            roleField.description = REQ_USER_FIELDS[3].description;
        } else {
            roleField.description = 'В рамках данной формы выбор роли - Участник или Помощник - не играет роли. Выбирайте всегда Участник.';
        }
        if (role === 'participant') {
            descField.name = ['DEFILE', 'SCENKA'].includes(nomination.type) ? 'Персонаж' : 'Пояснение';
            descField.description = descUchDescriptions[nomination.type];
        } else if (role === 'assistant') {
            descField.name = REQ_USER_FIELDS[4].name;
            if (['DEFILE', 'SCENKA', 'KARAOKE', 'DANCE'].includes(nomination.type)) {
                descField.description = REQ_USER_FIELDS[4].description;
            } else {
                descField.description = descUchDescriptions[nomination.type];
            }
        } else {
            descField.name = ['DEFILE', 'SCENKA'].includes(nomination.type) ? 'Персонаж/Пояснение' : 'Пояснение';
            if (['DEFILE', 'SCENKA', 'KARAOKE', 'DANCE'].includes(nomination.type)) {
                descField.description = descDescriptions[nomination.type];
            } else {
                descField.description = descUchDescriptions[nomination.type];
            }
        }
        const showTranscription = role === 'participant' && needTranscription &&
            (participantsCount < 3 || participantsCount === 3 && !isNewReqUser);
        if (showTranscription) {
            const transField = fields.find((field) => field.id === 'descTrans');
            if ((nomination.params?.maxParticipants || 100) > 3) {
                transField.description = transDescriptions.many;
            } else {
                transField.description = transDescriptions.few;
            }
            return [...fields];
        }
        return fields.filter((field) => field.id !== 'descTrans');
    }, [request, fields, values, isNewReqUser]);
}

export function FormReqUserEditField(props) {
    const { request, reqUser, submitting, onSubmit, onCancel, errors, clearErrors, ...otherProps } = props;
    const [reqUserFields, schema, initialData] = useInitData(reqUser, request.nomination);

    const handleCancelClick = useCallback(
        (e) => {
            if (onCancel) onCancel(e, reqUserFields[1]);
        },
        [onCancel, reqUserFields]
    );
    const handleFieldChange = useCallback(
        (val, name) => {
            if (errors && errors[name]) clearErrors();
        },
        [errors, clearErrors]
    );
    const handleSubmit = useCallback(
        (values) => {
            if (onSubmit) {
                const castedValues = schema.cast(values);
                if (castedValues && castedValues.role === 'assistant' && castedValues.descTrans) {
                    castedValues.descTrans = '';
                }
                if (reqUser) onSubmit({ ...castedValues, userId: reqUser.user.id });
                else onSubmit(castedValues);
            }
        },
        [onSubmit, reqUser, schema]
    );

    useEscCallback(handleCancelClick);

    const formik = useFormik({
        initialValues: initialData,
        validate: schema.validate,
        onSubmit: handleSubmit,
        validateOnMount: true,
    });
    const saveBtnDisabled = formik.isValidating || submitting || !formik.dirty || errors || !formik.isValid;
    const fields = useFormFields(reqUserFields, formik.values, request, reqUser);

    return (
        <Form onSubmit={formik.handleSubmit} noValidate>
            <div className="pt-2 border-top">
                {reqUser && (
                    <Form.Group>
                        <FormField value={formatReqUser(reqUser.user)} field={reqUserField} readOnly />
                    </Form.Group>
                )}
                {fields.map((field) => (
                    <Form.Group key={field.id}>
                        <FormField
                            {...otherProps}
                            field={field}
                            formik={formik}
                            onChange={handleFieldChange}
                            errors={errors}
                        />
                    </Form.Group>
                ))}
            </div>
            <Form.Row className="mt-3 mb-2">
                <Col>
                    <Button variant="outline-primary" type="submit" className="px-5 w-100" disabled={saveBtnDisabled}>
                        {submitting && (
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        )}
                        <span>Сохранить</span>
                    </Button>
                </Col>
                <Col>
                    <Button
                        variant="outline-secondary"
                        type="reset"
                        className="px-5 w-100"
                        onClick={handleCancelClick}
                        disabled={submitting}
                    >
                        Отмена
                    </Button>
                </Col>
            </Form.Row>
        </Form>
    );
}
